import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  DELETE_REVIEWER_LIST_ITEM,
  ErrorResponse,
  GET_IDEA_REVIEWER_LIST,
  IdeaReviewerList,
  User,
} from '@frontend/data-access';
import { App, Button, Modal, Table, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';
import AddExternal from './add-external';
import AddFromPlatform from './add-from-platform';

type ActionType = 'PLATFORM' | 'EXTERNAL';

function ReviewerList({ channelId }: { channelId: string }) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [data, setData] = useState<IdeaReviewerList[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [action, setAction] = useState<ActionType>();
  const [currentId, setCurrentId] = useState<string>();
  const [type, setType] = useState<'add' | 'edit'>('add');
  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [removeItem, { loading: removeLoading }] = useMutation(
    DELETE_REVIEWER_LIST_ITEM,
    {
      onError: handleError,
      onCompleted() {
        refetch();
      },
    }
  );
  const [getData, { loading, refetch }] = useLazyQuery<{
    ideaReviewerLists: {
      ideaReviewerListItem: IdeaReviewerList;
      requestStatus: boolean;
    }[];
  }>(GET_IDEA_REVIEWER_LIST, {
    onCompleted: (data) => {
      setData(data.ideaReviewerLists.map((item) => item.ideaReviewerListItem));
    },
    fetchPolicy: 'no-cache',
  });

  const columns: ColumnType<IdeaReviewerList>[] = [
    {
      title: t('no'),
      dataIndex: 'id',
      key: 'id',
      width: 50,
      render: (_: unknown, record: IdeaReviewerList, index: number) => {
        return index + 1;
      },
    },
    {
      title: t('full_name'),
      dataIndex: 'fullName',
      key: 'fullName',
      width: 150,
      ellipsis: true,
    },
    {
      title: t('email'),
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      width: 200,
    },
    {
      title: t('user_type'),
      dataIndex: 'user',
      key: 'user',
      width: 100,
      render: (record?: User) => {
        return record ? t('internal') : t('external');
      },
    },
    {
      title: t('title'),
      dataIndex: 'role',
      key: 'role',
      ellipsis: true,
      width: 150,
    },
    {
      title: t('label'),
      dataIndex: 'reviewerTag',
      key: 'reviewerTag',
      width: 120,
      render: (record: { name: string }) => {
        return record?.name ? (
          <div className="bg-blue-100 px-2 py-1 text-blue-600 rounded-sm text-xs w-fit whitespace-nowrap">
            {record.name}
          </div>
        ) : null;
      },
    },
    {
      title: t('note'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
    },
    {
      title: t('action'),
      dataIndex: 'action',
      width: 120,
      fixed: 'right',
      render: (_: unknown, record: IdeaReviewerList) => {
        return (
          <div className="space-x-3 whitespace-nowrap">
            <Typography.Link
              onClick={() =>
                handleModal(
                  record.user ? 'PLATFORM' : 'EXTERNAL',
                  record.id,
                  'edit'
                )
              }
              disabled={!channelId}
            >
              {t('edit')}
            </Typography.Link>
            <Typography.Link
              disabled={removeLoading || !channelId}
              onClick={() => handleClickDelete(record.id)}
            >
              {t('remove')}
            </Typography.Link>
          </div>
        );
      },
    },
  ];

  const handleClickDelete = (id: string) => {
    removeItem({
      variables: {
        input: {
          id,
          channelId,
        },
      },
    });
  };

  const handleFinished = () => {
    refetch();
    closeModal();
  };

  const renderAction = () => {
    switch (action) {
      case 'PLATFORM':
        return (
          <AddFromPlatform
            id={currentId}
            channelId={channelId}
            onFinish={handleFinished}
          />
        );
      case 'EXTERNAL':
        return (
          <AddExternal
            id={currentId}
            channelId={channelId}
            onFinish={handleFinished}
          />
        );
      default:
        return null;
    }
  };

  const renderTitle = () => {
    switch (action) {
      case 'PLATFORM':
        return type === 'add'
          ? t('add_from_platform')
          : t('edit_from_platform');
      case 'EXTERNAL':
        return type === 'add' ? t('add_external') : t('edit_external');
      default:
        return null;
    }
  };

  const handleModal = (
    action: ActionType,
    id?: string,
    type?: 'add' | 'edit'
  ) => {
    if (id) {
      setCurrentId(id);
    }
    setAction(action);
    setIsModalOpen(true);
    setType(type || 'add');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setAction(undefined);
    setCurrentId(undefined);
  };

  // Filter data based on `filterType`
  const filteredData = useMemo(
    () => data.filter((item) => item.addType === 'ADDED'),
    [data]
  );

  useEffect(() => {
    if (channelId) {
      getData({
        variables: {
          input: {
            channelId,
          },
        },
      });
    } else {
      setData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  return (
    <div className={!channelId ? 'cursor-not-allowed' : ''}>
      <div className={!channelId ? 'opacity-50' : ''}>
        <div className="text-gray-700">
          {t('add_person_to_idea_reviewer_list')}
        </div>
        <div className="mt-3 space-y-5">
          <div className="overflow-x-auto">
            <Table
              rowKey="id"
              size="small"
              columns={columns}
              loading={loading}
              dataSource={filteredData}
              pagination={false}
              scroll={{ x: 1200 }}
            />
          </div>
        </div>

        <div className="px-4 py-[10px] border-t border-gray-400 flex items-center gap-2 flex-wrap">
          <Button
            shape="round"
            icon={<PlusOutlined />}
            onClick={() => handleModal('EXTERNAL', undefined, 'add')}
            disabled={!channelId}
          >
            {t('add_external')}
          </Button>
          <Button
            shape="round"
            icon={<PlusOutlined />}
            onClick={() => handleModal('PLATFORM', undefined, 'add')}
            disabled={!channelId}
          >
            {t('add_from_platform')}
          </Button>
        </div>
      </div>

      {!channelId && (
        <div className="text-center text-gray-800 mt-4">
          {t('create_channel_first')}
        </div>
      )}

      <Modal
        title={renderTitle()}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width={1024}
      >
        {renderAction()}
      </Modal>
    </div>
  );
}

export default ReviewerList;
